import { createSlice } from '@reduxjs/toolkit';

/**
 * ReleaseBannerReducer
 * Manages global release banner state for the authenticated application.
 * This reducer is automatically cleared on logout via the RESET_ALL_REDUCERS
 * action handled in rootReducer.js.
 */
const initialState = {
    bannerData: null,       // Full release record from AppSync (ReleaseBanner table row)
    isVisible: false,       // Whether the banner should be displayed
    forceUpdate: false,     // Mirrors bannerData.ForceUpdate — drives banner behavior
    showLaterWarning: false,// Whether the "Later" warning message is shown
    loading: false,         // Query in-flight indicator
    error: null             // Error from query or subscription
};

export const ReleaseBannerReducer = createSlice({
    name: 'releaseBannerReducer',
    initialState,
    reducers: {
        /**
         * Called when the AppSync query is initiated.
         */
        fetchReleaseBannerInit: (state) => {
            state.loading = true;
            state.error = null;
        },

        /**
         * Called when a valid release banner record is received
         * (from the initial query or from a live subscription event).
         * @param {object} action.payload - ReleaseBanner record from AppSync
         */
        setReleaseBanner: (state, action) => {
            state.loading = false;
            state.bannerData = action.payload;
            state.isVisible = true;
            state.forceUpdate = action.payload?.forceUpdate === true;
            state.error = null;
        },

        /**
         * Called when no applicable release is found, or on explicit clear.
         * Resets banner to hidden state.
         */
        clearReleaseBanner: (state) => {
            state.loading = false;
            state.bannerData = null;
            state.isVisible = false;
            state.forceUpdate = false;
            state.showLaterWarning = false;
            state.error = null;
        },

        /**
         * Shows the "Later" warning message when user defers the update.
         */
        showLaterWarningAction: (state) => {
            state.showLaterWarning = true;
        },

        /**
         * Hides the "Later" warning — user acknowledged and continues.
         */
        hideLaterWarningAction: (state) => {
            state.showLaterWarning = false;
        },

        /**
         * Stores an error from the query or subscription.
         * @param {*} action.payload - Error object or message
         */
        setReleaseBannerError: (state, action) => {
            state.loading = false;
            state.error = action.payload;
        }
    }
});

export const {
    fetchReleaseBannerInit,
    setReleaseBanner,
    clearReleaseBanner,
    showLaterWarningAction,
    hideLaterWarningAction,
    setReleaseBannerError
} = ReleaseBannerReducer.actions;

// Selector — read entire releaseBannerReducer slice
export const getReleaseBannerSelector = (state) => state.releaseBannerReducer;

export default ReleaseBannerReducer.reducer;
